import React, {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import {useAppDispatch} from 'helpers/store'
import {OffersAction} from 'store/offers/OffersAction'
import BookingAction from 'store/booking/BookingAction'
import {DEFAULT_HALL_ID} from 'helpers/config'
import type {PackagesItemsResponse} from 'types/entities/PackagesItems'
import {PackageCategory} from 'types/enums/PackageCategory'
import type {Offer} from 'types/entities/Offer'
import type {OfferCalculation} from 'types/entities/OfferCalculation'
import {usePackageCategory} from 'utils/hooks/usePackageCategory'
import {getOfferPackageSwitchFailure, getOfferPackageSwitchItemIds, getSwitchableOfferCalculations, isSwitchCategoryAvailable, resolveOfferPackageReplacementItems} from 'utils/offers/offerPackageSwitch'
import {isOfferEditLocked} from 'utils/offers/offersUtils'
import {toast} from 'utils/toastUtils'

interface SwitchOfferPackageModalProps {
    offer: Offer
    calculation?: OfferCalculation
    initialCalculationId?: number
    onCancel: () => void
    onSwitched: () => void
}

const SwitchOfferPackageModal: React.FC<SwitchOfferPackageModalProps> = ({
    offer, calculation: calculationProp, initialCalculationId, onCancel, onSwitched,
}) => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()
    const {staticEventsForDisplay} = usePackageCategory()
    const calculations = getSwitchableOfferCalculations(offer)
    const [calculationId, setCalculationId] = useState(() =>
        calculations.find((item) => item.id === initialCalculationId)?.id ??
        calculations.find((item) => item.is_target)?.id ?? calculations[0]?.id)
    const calculation = calculationProp ?? calculations.find((item) => item.id === calculationId)
    const [packageCategory, setPackageCategory] = useState<string>(calculation?.packagesCart?.category ?? '')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submissionError, setSubmissionError] = useState<string>()
    const submittingRef = useRef(false)
    const catalogRequestRef = useRef(0)
    const [replacement, setReplacement] = useState<{catalog?: PackagesItemsResponse; loading?: boolean; error?: string}>({})
    const cart = calculation?.packagesCart
    const date = cart?.date || calculation?.date || calculation?.input?.date || offer.input?.date || ''
    const categories = staticEventsForDisplay.filter((category) =>
        category.id !== cart?.category && isSwitchCategoryAvailable(category, date,
            cart?.drivers_count_adult ?? 0, cart?.drivers_count_child ?? 0, cart?.category))
    const selectedCategoryIsValid = categories.some((category) => category.id === packageCategory)
    const disabled = isOfferEditLocked(offer.status) || !!calculation?.deleted_at || !cart?.id || !cart.token ||
        !getOfferPackageSwitchItemIds(cart).length ||
        !selectedCategoryIsValid || !replacement.catalog || replacement.loading || !!replacement.error

    const selectPackage = async (category: string) => {
        setPackageCategory(category)
        const requestId = ++catalogRequestRef.current
        setSubmissionError(undefined)
        setReplacement({})
        if (!cart || !categories.some((item) => item.id === category)) return
        setReplacement({loading: true})
        try {
            const activityIds = [...new Set((cart.packagesCartItems ?? [])
                .filter((item) => item.packageItem?.type === 'package_item')
                .map((item) => item.packageItem.activity_id).filter((id) => id > 0))]
            const catalog = await dispatch(BookingAction.fetchPackagesByCategoryAndTimeSlotForSeller({
                date,
                adults: cart.drivers_count_adult ?? 0,
                children: cart.drivers_count_child ?? 0,
                package_category_name: category as PackageCategory,
                hall_id: cart.hall_id ?? DEFAULT_HALL_ID,
                activity_ids: activityIds.length ? activityIds : [1],
                timeslot_id: cart.packagesCartItemSlots?.[0]?.timeslot_id ?? null,
            }, offer.seller_id, {storeResult: false}))
            if (requestId !== catalogRequestRef.current) return
            const {missingDurations} = resolveOfferPackageReplacementItems(cart, catalog)
            setReplacement(missingDurations.length
                ? {error: t('No matching access tickets for durations: {{durations}} minutes.', {durations: missingDurations.join(', ')})}
                : {catalog})
        } catch (error) {
            if (requestId === catalogRequestRef.current) setReplacement({error:
                getOfferPackageSwitchFailure(error).message ?? t('Failed to load available items')})
        }
    }

    const switchPackage = async () => {
        if (disabled || submittingRef.current) return
        submittingRef.current = true
        setSubmissionError(undefined)
        setIsSubmitting(true)
        try {
            await dispatch(OffersAction.fetchSwitchOfferCalculationPackage(
                calculation.id, offer.id, cart, packageCategory, replacement.catalog,
            ))
            toast.success(t('Package switched successfully'))
            onSwitched()
        } catch (error) {
            const failure = getOfferPackageSwitchFailure(error)
            const message = failure.message ?? t('Failed to switch package')
            setSubmissionError(message)
            // A rejected selection must be reconsidered before submitting it again.
            if (failure.status === 422) setReplacement({error: message})
            toast.error(message)
        } finally {
            submittingRef.current = false
            setIsSubmitting(false)
        }
    }

    return (
        <UiAlertConfirmationModal
            title={t('Switch Package')}
            message={
                <div className="space-y-4">
                    <div className="space-y-2">
                        <p>{t('Choose a new package for this calculation. Tickets keep the same duration, and prices are updated.')}</p>
                        <p>{t("Credits, extra activities and catering stay as they are. Check the package contents and prices after switching, especially children's prices.")}</p>
                    </div>
                    {!calculationProp && (
                        <label className="block space-y-2">
                            <span className="block font-semibold">{t('Calculation')}</span>
                            <select
                                className="w-full rounded-lg border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                value={calculationId ?? ''}
                                onChange={(event) => {
                                    const nextCalculationId = Number(event.target.value)
                                    setCalculationId(nextCalculationId)
                                    ++catalogRequestRef.current
                                    setReplacement({})
                                    setSubmissionError(undefined)
                                    setPackageCategory(calculations.find((item) => item.id === nextCalculationId)?.packagesCart?.category ?? '')
                                }}
                                disabled={isSubmitting}
                            >
                                {calculations.map((item, index) => (
                                    <option key={item.id} value={item.id}>{t('Calc')} {item.name || index + 1}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className="block space-y-2">
                        <span className="block font-semibold">{t('Package')}</span>
                        <select
                            className="w-full rounded-lg border border-gray-300 bg-white p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                            value={packageCategory}
                            onChange={(event) => {void selectPackage(event.target.value)}}
                            disabled={isSubmitting}
                        >
                            {cart?.category && (
                                <option value={cart.category}>
                                    {staticEventsForDisplay.find((category) => category.id === cart.category)?.title ?? cart.category}
                                </option>
                            )}
                            {categories.map((category) => <option key={category.id} value={category.id}>{category.title}</option>)}
                        </select>
                    </label>
                    {replacement.loading && <p role="status">{t('Loading...')}</p>}
                    {replacement.error && <p role="alert" className="text-red-600 dark:text-red-400">{replacement.error}</p>}
                    {submissionError && !replacement.error && <p role="alert" className="text-red-600 dark:text-red-400">{submissionError}</p>}
                </div>
            }
            onConfirm={switchPackage}
            onCancel={onCancel}
            confirmLabel={t('Switch Package')}
            cancelLabel={t('Cancel')}
            confirmDisabled={disabled}
            isSubmitting={isSubmitting}
        />
    )
}

export default SwitchOfferPackageModal
